import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomDocumentsContext from '../providers/DataRoomDocuments/DataRoomDocuments.context';
import DataRoomSelectedDocumentsContext from '../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DeleteFilesModal from '../../DeleteFilesModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getArrayLength } from 'utils';
import useNodes from 'hooks/useNodes';

type UseDeleteFileProps = {
  setIsRequestPending?: (isPending: boolean) => void;
  onSuccess?: () => void;
};

const useDeleteFile = ({
  setIsRequestPending,
  onSuccess,
}: UseDeleteFileProps = {}) => {
  const [nodes, setNodes] = useState<DataRoomNode[]>([]);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { setCurrentlyDisplayedNodes } = useContext(DataRoomDocumentsContext);
  const { removeAll } = useContext(DataRoomSelectedDocumentsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const { getCopyByNodeType } = useNodes(nodes);

  const count = useMemo(() => getArrayLength(nodes), [nodes]);

  const nodeIds = useMemo(() => nodes.map(({ _id }) => _id), [nodes]);

  const type = useMemo(
    () =>
      getCopyByNodeType(
        'numberOfItems',
        'numberOfFolders',
        'numberOfFiles',
        count,
      ),
    [count, getCopyByNodeType],
  );

  const { t } = useTranslation();

  const docDeleteRequest = useCallback(async () => {
    try {
      setIsRequestPending?.(true);

      await dataRoomApi.nodes.bulkDeleteNodes(nodeIds);

      modal?.close();
      popSuccess(t('successfullyDeletedNode', { type }));
      setCurrentlyDisplayedNodes?.((old) =>
        old.filter(({ _id }) => !nodeIds.includes(_id)),
      );
      onSuccess?.();
      removeAll?.();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsRequestPending?.(false);
    }
  }, [
    nodeIds,
    type,
    modal,
    setIsRequestPending,
    popSuccess,
    t,
    setCurrentlyDisplayedNodes,
    removeAll,
    popServerError,
    onSuccess,
  ]);

  const onDocDelete = useCallback(
    async (nodes: DataRoomNode[]) => {
      setNodes(nodes);
      modal?.open();
    },
    [modal],
  );

  const DeleteDocumentsModal = useMemo(
    () => (
      <DeleteFilesModal
        ref={modalRef}
        nodes={nodeIds}
        type={type}
        onDelete={docDeleteRequest}
      />
    ),
    [nodeIds, type, modalRef, docDeleteRequest],
  );

  return { onDocDelete, DeleteDocumentsModal };
};

export default useDeleteFile;
